/**
 * 面接中の聞き取りゲージ（#23）。課題文のタスクごとに区切ったバーと星で進み具合を示す。
 * 面接画面（スマホ・PC）と課題文ビューで共有する。
 */
import {
  PROGRESS_TARGET_RATIO,
  isScoredPart,
  type InterviewProgress,
  type LiveScore,
  type LiveScorePart,
  type TaskProgress,
} from "../lib/interview-task-progress";
import "./InterviewProgressGauge.css";

const COLORS = {
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  sub: "#52525B",
  line: "#E4E4E7",
  cyan: "#0E7490",
  cyanSoft: "#ECFEFF",
  green: "#15803D",
  greenSoft: "#DCFCE7",
  greenLine: "#86EFAC",
};

const STAR_PATH = "M12 2.8l2.8 5.7 6.3.9-4.5 4.4 1 6.3L12 17.1 6.4 20.1l1-6.3L2.9 9.4l6.3-.9z";

export interface ProgressGain {
  /** 同じ内容でも演出を最初から流すための連番 */
  key: number;
  /** 今回増えた項目数 */
  amount: number;
  label: string;
}

function Star({ on, size, pop }: { on: boolean; size: number; pop?: boolean }) {
  return (
    <svg
      className={pop ? "interview-progress-starpop" : undefined}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      {on ? (
        <path d={STAR_PATH} fill="#F59E0B" stroke="#B45309" strokeWidth={1} strokeLinejoin="round" />
      ) : (
        <path d={STAR_PATH} fill="none" stroke="#A1A1AA" strokeWidth={1.5} strokeLinejoin="round" />
      )}
    </svg>
  );
}

function barColor(task: { done: boolean }) {
  return task.done ? COLORS.green : COLORS.cyan;
}

function countableTasks(progress: InterviewProgress): TaskProgress[] {
  return progress.tasks.filter((task) => task.total > 0);
}

/** 目安（8割）まで聞けたとき・全部聞けたときの案内。 */
function progressMessage(progress: InterviewProgress): string | null {
  if (progress.complete) return "必要なことはすべて聞けました。あいさつをして、歯科衛生士への報告に進みましょう。";
  if (progress.reachedTarget) {
    return `目安の${Math.round(PROGRESS_TARGET_RATIO * 100)}%まで聞けました。報告に進んでも大丈夫です。残りを聞くとさらに点が伸びます。`;
  }
  return null;
}

export function ProgressStars({
  progress,
  size = 18,
  popTaskNo,
}: {
  progress: InterviewProgress;
  size?: number;
  popTaskNo?: number | null;
}) {
  const tasks = countableTasks(progress);
  if (tasks.length === 0) return null;
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 2 }}
      aria-label={`達成したタスク ${progress.doneTasks} / ${tasks.length}`}
    >
      {tasks.map((task) => (
        <Star key={task.no} on={task.done} size={size} pop={popTaskNo === task.no} />
      ))}
    </div>
  );
}

export function ProgressBar({
  progress,
  height = 12,
  shine = false,
  label = "聞き取りの進み具合",
}: {
  progress: InterviewProgress;
  height?: number;
  shine?: boolean;
  label?: string;
}) {
  const ratio = progress.total > 0 ? progress.got / progress.total : 0;
  return (
    <div style={{ position: "relative" }}>
      <div
        className={shine ? "interview-progress-shine" : undefined}
        style={{ height, borderRadius: 999, background: COLORS.line, overflow: "hidden" }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.got}
        aria-label={label}
      >
        <div
          className="interview-progress-fill"
          style={{
            width: `${Math.round(ratio * 100)}%`,
            background: progress.reachedTarget ? COLORS.green : COLORS.cyan,
          }}
        />
      </div>
      {/* 報告に進んでよい目安の線 */}
      <div
        aria-hidden="true"
        title={`目安 ${Math.round(PROGRESS_TARGET_RATIO * 100)}%`}
        style={{
          position: "absolute",
          left: `${PROGRESS_TARGET_RATIO * 100}%`,
          top: -3,
          bottom: -3,
          width: 2,
          marginLeft: -1,
          borderRadius: 1,
          background: progress.reachedTarget ? COLORS.green : COLORS.ink,
          opacity: 0.55,
        }}
      />
    </div>
  );
}

/** 面接画面のゲージ。compact はスマホ、regular はPCのアバター上。 */
export function InterviewProgressGauge({
  progress,
  variant = "compact",
  gain,
}: {
  progress: InterviewProgress;
  variant?: "compact" | "regular";
  gain?: ProgressGain | null;
}) {
  const percent = progress.total > 0 ? Math.round((progress.got / progress.total) * 100) : 0;
  const message = progressMessage(progress);
  const regular = variant === "regular";
  return (
    <div
      data-testid="interview-progress-gauge"
      style={{
        position: "relative",
        padding: regular ? "12px 14px" : 12,
        background: regular ? "rgba(255,255,255,0.96)" : COLORS.surface,
        border: `1px solid ${progress.reachedTarget ? COLORS.greenLine : COLORS.line}`,
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        color: COLORS.ink,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: regular ? 13 : 12, fontWeight: 800 }}>
          聞き取り{" "}
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              color: progress.complete ? COLORS.green : COLORS.cyan,
            }}
          >
            {progress.got} / {progress.total}
          </span>
        </div>
        <div style={{ fontSize: 11, color: regular ? COLORS.sub : COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
          <b style={{ fontSize: regular ? 14 : 13, color: progress.reachedTarget ? COLORS.green : COLORS.ink }}>{percent}%</b>
          {"　"}目安 {Math.round(PROGRESS_TARGET_RATIO * 100)}%
        </div>
      </div>
      <ProgressBar progress={progress} shine={!!gain} />
      {message && (
        <div
          data-testid="interview-progress-message"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "10px 12px",
            borderRadius: 10,
            background: COLORS.greenSoft,
            color: COLORS.green,
            fontSize: 12.5,
            fontWeight: 700,
            lineHeight: 1.6,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.green} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          {message}
        </div>
      )}
      {gain && (
        <div
          key={gain.key}
          className="interview-progress-gain"
          aria-live="polite"
          style={{
            position: "absolute",
            right: 12,
            top: -14,
            maxWidth: "calc(100% - 24px)",
            padding: "4px 10px",
            borderRadius: 999,
            background: COLORS.cyan,
            color: "#FFFFFF",
            fontSize: 12,
            fontWeight: 800,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            boxShadow: "0 4px 12px rgba(14,116,144,.35)",
            pointerEvents: "none",
          }}
        >
          +{gain.amount}　{gain.label}
        </div>
      )}
    </div>
  );
}

/** 課題文ビュー上部の進み具合カード。 */
export type ProgressKind = "interview" | "report";

const CARD_COPY: Record<ProgressKind, {
  title: string;
  countLabel: string;
  complete: string;
  reached: string;
  pending: string;
}> = {
  interview: {
    title: "聞き取りの進み具合",
    countLabel: "聞けた項目",
    complete: "すべて聞けました。歯科衛生士への報告に進めます。",
    reached: `目安の${Math.round(PROGRESS_TARGET_RATIO * 100)}%を超えました。報告に進んでも大丈夫です。`,
    pending: `目安は${Math.round(PROGRESS_TARGET_RATIO * 100)}%（バーの線）。超えたら報告に進めます。`,
  },
  report: {
    title: "報告の進み具合",
    countLabel: "報告の点（聞き返されると減点）",
    complete: "すべて報告できました。採点に進めます。",
    reached: `報告で目安の${Math.round(PROGRESS_TARGET_RATIO * 100)}%を超えました。`,
    pending: `目安は${Math.round(PROGRESS_TARGET_RATIO * 100)}%（バーの線）。聞き取ったことを、聞き返される前に伝えましょう。`,
  },
};

export function InstructionProgressCard({
  progress,
  kind = "interview",
}: {
  progress: InterviewProgress;
  kind?: ProgressKind;
}) {
  const copy = CARD_COPY[kind];
  const tasks = countableTasks(progress);
  return (
    <div
      data-testid="instruction-progress-card"
      style={{
        background: COLORS.surface,
        border: `1px solid ${progress.complete ? COLORS.greenLine : COLORS.line}`,
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800 }}>{copy.title}</div>
        {tasks.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ProgressStars progress={progress} size={20} />
            <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
              {progress.doneTasks} / {tasks.length} タスク
            </span>
          </div>
        )}
      </div>
      <ProgressBar progress={progress} height={10} label={copy.title} />
      <div style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.6 }}>
        {copy.countLabel}{" "}
        <b style={{ color: COLORS.ink, fontVariantNumeric: "tabular-nums" }}>
          {progress.got} / {progress.total}
        </b>
        。
        {progress.complete ? copy.complete : progress.reachedTarget ? copy.reached : copy.pending}
      </div>
    </div>
  );
}

/** 課題文のタスク行に付けるバッジと点。 */
export function TaskProgressBadge({ task }: { task: TaskProgress }) {
  if (task.done) {
    return (
      <span
        style={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 10px",
          borderRadius: 999,
          background: COLORS.greenSoft,
          color: COLORS.green,
          fontSize: 12,
          fontWeight: 800,
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
          <path d={STAR_PATH} fill={COLORS.green} />
        </svg>
        達成
      </span>
    );
  }
  return (
    <span
      style={{
        flexShrink: 0,
        padding: "4px 10px",
        borderRadius: 999,
        background: task.got > 0 ? COLORS.cyanSoft : "#F4F4F5",
        color: task.got > 0 ? COLORS.cyan : COLORS.muted,
        fontSize: 12,
        fontWeight: 800,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {task.got} / {task.total}
    </span>
  );
}

export function TaskProgressDots({ task }: { task: TaskProgress }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, paddingTop: 6 }} aria-hidden="true">
      {Array.from({ length: task.total }, (_, index) => (
        <span
          key={index}
          style={{
            width: 18,
            height: 6,
            borderRadius: 999,
            background: index < task.got ? barColor(task) : COLORS.line,
          }}
        />
      ))}
    </div>
  );
}

/** ドックの「課題文」ボタンに重ねる達成数のバッジ。 */
export function DockProgressBadge({ progress }: { progress: InterviewProgress }) {
  const total = progress.countableTasks;
  if (total === 0) return null;
  return (
    <span
      aria-label={`達成したタスク ${progress.doneTasks} / ${total}`}
      style={{
        position: "absolute",
        top: 4,
        right: 6,
        minWidth: 20,
        height: 20,
        padding: "0 5px",
        boxSizing: "border-box",
        borderRadius: 999,
        background: progress.complete ? COLORS.green : COLORS.cyan,
        color: "#FFFFFF",
        fontSize: 10,
        fontWeight: 800,
        display: "grid",
        placeItems: "center",
        fontVariantNumeric: "tabular-nums",
        pointerEvents: "none",
      }}
    >
      {progress.doneTasks}/{total}
    </span>
  );
}

function ScoreRow({
  label,
  part,
  muted = false,
  showTarget = false,
  note,
}: {
  label: string;
  part: LiveScorePart;
  muted?: boolean;
  showTarget?: boolean;
  note?: string;
}) {
  const scored = isScoredPart(part);
  const ratio = scored ? Math.max(0, Math.min(1, part.total / part.max)) : 0;
  const percent = Math.round(ratio * 100);
  const reached = scored && ratio >= PROGRESS_TARGET_RATIO;
  const fill = muted ? "#A1A1AA" : showTarget && reached ? COLORS.green : COLORS.cyan;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: muted ? COLORS.muted : COLORS.ink }}>
          {label}
          {note && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: COLORS.muted }}>{note}</span>}
        </div>
        <div style={{ fontSize: 11, color: COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
          <b style={{ fontSize: 13, color: showTarget && reached ? COLORS.green : muted ? COLORS.muted : COLORS.ink }}>
            {scored ? `${percent}%` : "—"}
          </b>
          {showTarget && <>{"　"}目安 {Math.round(PROGRESS_TARGET_RATIO * 100)}%</>}
        </div>
      </div>
      <div style={{ position: "relative" }}>
        <div
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          style={{ height: showTarget ? 12 : 8, borderRadius: 999, background: COLORS.line, overflow: "hidden" }}
        >
          <div className="interview-progress-fill" style={{ width: `${percent}%`, background: fill }} />
        </div>
        {showTarget && (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: `${PROGRESS_TARGET_RATIO * 100}%`,
              top: -3,
              bottom: -3,
              width: 2,
              marginLeft: -1,
              borderRadius: 1,
              background: reached ? COLORS.green : COLORS.ink,
              opacity: 0.55,
            }}
          />
        )}
      </div>
    </div>
  );
}

/** 総合の点数が目安（8割）に届いたか。 */
export function isOverallReady(liveScore: LiveScore | null | undefined): boolean {
  const overall = liveScore?.overall;
  return isScoredPart(overall) && overall.total / overall.max >= PROGRESS_TARGET_RATIO;
}

/**
 * 報告面接中の3本: 患者面接の結果（固定）・報告の進み具合・総合（8割の目安つき）。
 * 数字は採点と同じ計算（/api/current-interview/live-score）。
 */
export function ReportScoreGauges({
  liveScore,
  variant = "compact",
}: {
  liveScore: LiveScore;
  variant?: "compact" | "regular";
}) {
  const ready = isOverallReady(liveScore);
  // 総合は報告で点が出てから出す。報告前は患者面接の点だけで計算されて紛らわしいため
  const hasReportScore = isScoredPart(liveScore.dentist) && liveScore.dentist.total > 0;
  return (
    <div
      data-testid="report-score-gauges"
      style={{
        padding: variant === "regular" ? "12px 14px" : 12,
        background: variant === "regular" ? "rgba(255,255,255,0.96)" : COLORS.surface,
        border: `1px solid ${ready ? COLORS.greenLine : COLORS.line}`,
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: COLORS.ink,
      }}
    >
      <ScoreRow label="患者面接" note="結果" part={liveScore.patient} muted />
      <ScoreRow label="報告" note="聞き返されると減点" part={liveScore.dentist} />
      {hasReportScore ? (
        <>
          <div style={{ borderTop: `1px dashed ${COLORS.line}` }} />
          <ScoreRow label="総合" part={liveScore.overall} showTarget />
        </>
      ) : (
        <div style={{ fontSize: 11.5, color: COLORS.muted }}>報告で点が入ると、総合点を表示します。</div>
      )}
      {ready && (
        <div
          data-testid="report-score-message"
          style={{
            padding: "8px 12px",
            borderRadius: 10,
            background: COLORS.greenSoft,
            color: COLORS.green,
            fontSize: 12.5,
            fontWeight: 700,
            lineHeight: 1.6,
          }}
        >
          総合で目安の{Math.round(PROGRESS_TARGET_RATIO * 100)}%を超えました。採点に進んでも大丈夫です。
        </div>
      )}
    </div>
  );
}
